import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import type { ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { useModuleSubView } from '../useModuleSubView'
import { TravelExpenseSummaryTable } from './components/TravelExpenseSummaryTable'
import { TravelExpenseAllEntriesPage } from './TravelExpenseAllEntriesPage'
import { TravelExpenseEmployeeEntriesPage } from './TravelExpenseEmployeeEntriesPage'
import { parseMonth } from './travelExpenseFormat'

interface Props {
  module: ServiceModuleModel
}

/**
 * Travel expense module (Reisekosten) as its own page. Employees record their travel days in the
 * app, managers only read: monthly overview per employee, all entries, entries of one employee.
 * Sub-views are URL-driven like time tracking (`useModuleSubView`).
 */
export function TravelExpenseServiceModulePage({ module }: Props) {
  const { t } = useTranslation('service-modules-travel-expenses')
  const { view, showOverview, showAll, showEmployee } = useModuleSubView()
  const [month, setMonth] = useState(() => dayjs().format('YYYY-MM'))
  const parsedMonth = parseMonth(month)

  if (view.mode === 'all') {
    return <TravelExpenseAllEntriesPage module={module} onBack={showOverview} />
  }
  if (view.mode === 'employee') {
    return (
      <TravelExpenseEmployeeEntriesPage
        module={module}
        employeeId={view.employeeId}
        month={month}
        onBack={showOverview}
      />
    )
  }

  return (
    <PageLayout>
      <PageHeader title={module.name || t('title')} description={t('description')}>
        <PageHeaderActions>
          <Button onClick={showAll}>{t('showAll')}</Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <Input
          type="month"
          className="w-48 h-9"
          aria-label={t('summary.month')}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </FilterRow>

      {parsedMonth && (
        <TravelExpenseSummaryTable
          serviceModuleId={module.id}
          year={parsedMonth.year}
          month={parsedMonth.month}
          onRowClick={showEmployee}
        />
      )}
    </PageLayout>
  )
}
